/**
 * L4 — the deterministic avatar hue.
 *
 * ===========================================================================
 * THE ONLY PROPERTY THAT MATTERS IS STABILITY
 * ===========================================================================
 * The point of colouring avatars is recognition: Shailesh is the same colour in
 * the topbar, on a board card, in the activity feed and on the record page, so
 * the eye can find him without reading. A hash that drifts — between renders,
 * between sessions, between two people's machines — does not merely fail to
 * help, it actively misleads, because the user has already learned that green
 * means Shailesh. A drifting hash is worse than one flat colour for everybody.
 *
 * So this module is deliberately boring, and every part of it is chosen for
 * reproducibility rather than speed or elegance:
 *
 *  · FNV-1a/32 over UTF-16 code units, both bytes mixed. No `Math.random`, no
 *    `Date`, no counter, no module-level mutable state, no `Intl`, no
 *    locale-sensitive casing beyond `toLowerCase` on an id we control.
 *  · `Math.imul` for every multiply. Plain `*` on a 32-bit product silently
 *    goes through a float64 and loses the low bits differently once the value
 *    exceeds 2^53 — `Math.imul` is exact 32-bit on every engine, which is what
 *    "same on every machine" actually requires.
 *  · A MurmurHash3 fmix32 avalanche before the modulo. FNV-1a's low bits are
 *    the weakest part of the digest and `% 8` reads nothing else; without the
 *    finaliser, ids that share a suffix (`…-0001`, `…-0002`) land in a visible
 *    stripe pattern rather than spreading. The avalanche is why the spread test
 *    passes on sequential ids and not just on random ones.
 *
 * There is no seed, no config and no way to force a particular slot. That is
 * intentional: a caller who can pick the colour will eventually pick it to mean
 * something, and the moment avatar colour means something it collides with the
 * stage-colour spine, which is the one thing in this app allowed to mean colour.
 */

/** The number of hue slots defined in `tokens.css`. Not a tunable — the tokens
 *  and `AVATAR_HUE_CLASSES` must be extended together, and the tests check it. */
export const AVATAR_HUE_COUNT = 8;

/**
 * Slot → utility pair, written out in full.
 *
 * THESE STRINGS MUST STAY LITERAL. Tailwind v4 finds classes by scanning source
 * text, so a template like `bg-avatar-${slot}-bg` produces a class that is never
 * generated and an avatar with no colour at all. The array is the reason the
 * slot count is not a loop variable anywhere in this file.
 */
export const AVATAR_HUE_CLASSES: readonly string[] = [
  'bg-avatar-1-bg text-avatar-1-fg',
  'bg-avatar-2-bg text-avatar-2-fg',
  'bg-avatar-3-bg text-avatar-3-fg',
  'bg-avatar-4-bg text-avatar-4-fg',
  'bg-avatar-5-bg text-avatar-5-fg',
  'bg-avatar-6-bg text-avatar-6-fg',
  'bg-avatar-7-bg text-avatar-7-fg',
  'bg-avatar-8-bg text-avatar-8-fg',
];

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** FNV-1a/32 over UTF-16 code units, low byte then high byte. */
function fnv1a32(input: string): number {
  let hash = FNV_OFFSET_BASIS;
  for (let index = 0; index < input.length; index += 1) {
    const code = input.charCodeAt(index);
    hash = Math.imul(hash ^ (code & 0xff), FNV_PRIME);
    hash = Math.imul(hash ^ (code >>> 8), FNV_PRIME);
  }
  return hash >>> 0;
}

/** MurmurHash3 fmix32 — spreads the digest so the low three bits are usable. */
function avalanche(input: number): number {
  let hash = input;
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  return hash >>> 0;
}

/**
 * The slot, 0…7, for a stable identity string.
 *
 * `identity` should be the user's id. It is lower-cased and trimmed first so a
 * caller who can only reach the display name still gets one answer for
 * "Shailesh Patel" and "shailesh patel ".
 *
 * The empty string is NOT special-cased. It hashes like anything else, to one
 * fixed slot, so every unknown person shares a colour instead of each unknown
 * getting a different one — which is the honest rendering of "we do not know
 * who this is".
 */
export function avatarHueIndex(identity: string): number {
  return avalanche(fnv1a32(identity.trim().toLowerCase())) % AVATAR_HUE_COUNT;
}

/** The `bg-…`/`text-…` utility pair for an identity. */
export function avatarHueClasses(identity: string): string {
  return AVATAR_HUE_CLASSES[avatarHueIndex(identity)] ?? AVATAR_HUE_CLASSES[0] ?? '';
}
